import {
    type ReactNode,
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type IPieChartLegendProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import ChartKey from "../../../chart/components/key";
import Text from "../../../text";

const PieChartLegendRow = ({
    onActiveChange,
    customTheme,
    children,
    isActive,
    itemKey,
    style
}: {
    onActiveChange: IPieChartLegendProps["onActiveChange"];
    customTheme: IPieChartLegendProps["customTheme"];
    style: IPieChartLegendProps["style"];
    children: ReactNode;
    isActive: boolean;
    itemKey: string;
}) => {
    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        onHoverOut: () => onActiveChange(undefined),
        onHoverIn: () => onActiveChange(itemKey),
        onBlur: () => onActiveChange(undefined),
        onFocus: () => onActiveChange(itemKey),
        isDisabled: false
    });

    return <View
        role="listitem"
    >
        <Pressable
            {...interactionHandlers}
            onPress={() => onActiveChange(itemKey)}
            style={[
                style,
                getFocusRingStyle({
                    isFocusVisible,
                    borders,
                    colors
                })
            ]}
            aria-pressed={isActive}
            role="button"
        >
            {children}
        </Pressable>
    </View>;
};

const PieChartLegend: FC<IPieChartLegendProps> = ({
    onActiveChange,
    customTheme,
    shareWidth,
    valueWidth,
    activeKey,
    items,
    style,
    ...props
}) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeRow: activeRowDynamicStyle,
        share: shareDynamicStyle,
        value: valueDynamicStyle,
        row: rowDynamicStyle
    } = useStyles({
        shareWidth,
        valueWidth,
        radiuses,
        colors,
        spaces
    });

    return <View
        {...props}
        style={[
            style,
            stylesheet.container
        ]}
        role="list"
    >
        {
            items.map((item) => <PieChartLegendRow
                style={[
                    stylesheet.row,
                    rowDynamicStyle,
                    activeKey === item.key ? activeRowDynamicStyle : undefined
                ]}
                isActive={activeKey === item.key}
                onActiveChange={onActiveChange}
                customTheme={customTheme}
                itemKey={item.key}
                key={item.key}
            >
                <ChartKey
                    colorIndex={item.colorIndex}
                    customTheme={customTheme}
                    pattern={item.pattern}
                    shape="square"
                />
                <Text
                    color={activeKey === item.key ? "high" : "mid"}
                    customTheme={customTheme}
                    style={stylesheet.label}
                    variant="bodySmallSize"
                    numberOfLines={1}
                >
                    {item.title}
                </Text>
                <View
                    style={[
                        stylesheet.column,
                        valueDynamicStyle
                    ]}
                >
                    <Text
                        style={stylesheet.number}
                        customTheme={customTheme}
                        variant="bodySmallSize"
                        color="high"
                    >
                        {item.value}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.column,
                        shareDynamicStyle
                    ]}
                >
                    <Text
                        style={stylesheet.number}
                        customTheme={customTheme}
                        variant="bodySmallSize"
                        color="low"
                    >
                        {item.share}
                    </Text>
                </View>
            </PieChartLegendRow>)
        }
    </View>;
};
export default PieChartLegend;
